"use client"

import { useEffect, useRef, useState, useTransition } from "react"

import { LoadingOverlay } from "@/components/compass/LoadingOverlay"
import { useToast } from "@/components/compass/Toast"

export type Empresa = {
  id: string
  nome: string
}

/*
 * Espelha o ActionResult de (app)/empresa-actions.ts. Este componente é do
 * design system e não importa server action; o contrato entra pelo tipo.
 * Sucesso não tem payload aqui: a action redireciona e o shell remonta.
 */
export type TrocaEmpresaResultado = { ok: true } | { ok: false; error: string }

type EmpresaSelectorProps = {
  empresas: readonly Empresa[]
  activeEmpresaId: string
  // PM-Switch-01: troca de empresa (server action injetada pelo layout via
  // Header). Opcional: consumers legados (design system) seguem cosméticos.
  onSelect?:
    | ((
        empresaId: string,
      ) =>
        | void
        | TrocaEmpresaResultado
        | Promise<void | TrocaEmpresaResultado>)
    | undefined
}

const FALHA_INESPERADA = "Não foi possível trocar de empresa. Tente de novo."

// PM-X5a: tenant renderizado como "Empresa". PM-Switch-01 liga a troca:
// clique num item dispara onSelect dentro de useTransition (itens
// desabilitados enquanto pende; a action redireciona e remonta o shell).
export function EmpresaSelector({
  empresas,
  activeEmpresaId,
  onSelect,
}: EmpresaSelectorProps) {
  const [open, setOpen] = useState(false)
  const [pending, startTransition] = useTransition()
  const containerRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()
  const active =
    empresas.find((e) => e.id === activeEmpresaId) ?? empresas[0] ?? null
  const multi = empresas.length > 1

  useEffect(() => {
    if (!open) return
    function onClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => multi && setOpen((v) => !v)}
        aria-haspopup={multi ? "menu" : undefined}
        aria-expanded={multi ? open : undefined}
        aria-label="Empresa ativa"
        disabled={!multi}
        className="flex items-center gap-2 rounded border border-markv-surface-200 bg-markv-surface-50 px-3 py-1.5 text-sm text-markv-text-700 hover:bg-markv-surface-100 disabled:cursor-default disabled:hover:bg-markv-surface-50"
      >
        <span
          aria-hidden="true"
          className="text-xs uppercase tracking-wide text-markv-text-400"
        >
          Empresa
        </span>
        <span className="max-w-[160px] truncate font-medium">
          {active?.nome ?? "Sem empresa"}
        </span>
        {multi ? (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        ) : null}
      </button>

      {open && multi ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-56 rounded border border-markv-surface-200 bg-markv-surface-50 py-1 shadow-md"
        >
          {empresas.map((empresa) => (
            <button
              key={empresa.id}
              type="button"
              role="menuitemradio"
              aria-checked={empresa.id === activeEmpresaId}
              disabled={pending}
              onClick={() => {
                setOpen(false)
                if (empresa.id === activeEmpresaId || !onSelect) return
                // PM-Switch-02: a falha morre AQUI, virando toast.
                //
                // Sem o try/catch isto derrubava o app inteiro. onSelect é uma
                // Server Action dentro de startTransition, e no React 19 uma
                // action que rejeita é relançada no error boundary mais
                // próximo — que daqui é o ErrorBoundary de TELA CHEIA do
                // (app)/layout.tsx. Em prod (14/ago/2026) o servidor
                // respondia 303 e 200 sem nenhum 5xx e a pessoa via mesmo
                // assim "Algo deu errado por aqui", sem sidebar e sem header:
                // um tropeço na troca apagava o workspace.
                //
                // Sucesso não passa por aqui: a action redireciona e o shell
                // remonta antes de qualquer resultado voltar.
                startTransition(async () => {
                  try {
                    const resultado = await onSelect(empresa.id)
                    if (resultado && !resultado.ok) {
                      toast({ title: resultado.error, variant: "danger" })
                    }
                  } catch {
                    toast({ title: FALHA_INESPERADA, variant: "danger" })
                  }
                })
              }}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-markv-surface-100 disabled:cursor-wait disabled:opacity-60 ${
                empresa.id === activeEmpresaId
                  ? "text-markv-primary-accent"
                  : "text-markv-text-700"
              }`}
            >
              <span className="truncate">{empresa.nome}</span>
              {empresa.id === activeEmpresaId ? (
                <span aria-hidden="true" className="text-xs">
                  ●
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {/* Trocar de empresa recarrega o app inteiro, então a espera é de TELA:
          um overlay de área ficaria preso a este dropdown e cobriria 200px
          enquanto o resto da tela segue mostrando os dados da empresa
          anterior, que é pior do que não mostrar nada. */}
      <LoadingOverlay open={pending} label="Trocando de empresa" cobertura="tela" />
    </div>
  )
}
